import React, { useEffect, useState } from 'react';
// TODO: Switch to @imminently/decisively-ui/Popover when that component is ready.
import { Popover, PopoverTrigger, PopoverContent } from '@/components/radix/Popover/Popover';
import { useCommandPaletteContext } from '../CommandPaletteContext';
import { keyboardShortcutManager, formatShortcut, isMac, isModKey } from '../utils/keyboardShortcuts';
import styles from '../CommandPalette.module.scss';

export interface CommandActionProps {
  /** Display label for the action. */
  text: string;
  /** Shortcut key string (e.g. `'mod+k'`, `'enter'`) — rendered via formatShortcut. */
  shortcutKey: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * Displays a text label alongside a formatted keyboard shortcut badge.
 * Renders as a `<button>`. When no `onClick` is provided pointer events are
 * disabled so it can be composed inside another interactive element without
 * interfering.
 */
export const CommandAction: React.FC<CommandActionProps> = ({ text, shortcutKey, onClick, ...props }) => (
  <button
    {...props}
    type="button"
    className={styles.commandAction}
    onClick={onClick}
    style={onClick ? undefined : { pointerEvents: 'none' }}
    tabIndex={onClick ? undefined : -1}
  >
    <span className={styles.commandActionText}>{text}</span>
    <kbd className={styles.footerKbd}>{formatShortcut(shortcutKey)}</kbd>
  </button>
);

/**
 * Actions popover — lists all registered keyboard shortcuts.
 * Opens via the trigger button or mod+k while the command palette is open.
 */
export const CommandActions: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { isOpen: paletteIsOpen, registerShortcut } = useCommandPaletteContext();

  // Register mod+k globally so it appears in the Actions list.
  // useEffect(() => {
  //   return registerShortcut('mod+k', () => setOpen(v => !v), 'Actions');
  // }, [registerShortcut]);

  // Within the palette the cmdk input suppresses the global shortcut handler,
  // so we attach a capture-phase listener to intercept mod+k directly.
  useEffect(() => {
    if (!paletteIsOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isModKey(e) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(v => !v);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [paletteIsOpen]);

  // Close the popover when the palette closes.
  useEffect(() => {
    if (!paletteIsOpen) setOpen(false);
  }, [paletteIsOpen]);

  const shortcuts = keyboardShortcutManager.getRegisteredShortcuts();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <CommandAction
          text="Actions"
          shortcutKey="mod+k"
          onClick={() => setOpen(v => !v)}
        />
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className={styles.shortcutsPopover}>
        <p className={styles.shortcutsPopoverHeading}>Actions</p>
        {shortcuts.length > 0 ? (
          <ul className={styles.shortcutsList}>
            {shortcuts.map(entry => (
              <li key={entry.shortcut} className={styles.shortcutsItem}>
                <CommandAction text={entry.label} shortcutKey={entry.shortcut} />
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.shortcutsEmpty}>No actions registered.</p>
        )}
      </PopoverContent>
    </Popover>
  );
};
